import { Controller } from "@hotwired/stimulus"

// Shared diagnostic log viewer for PWA apps.
// Reads structured log entries from IndexedDB and renders them with filtering.
//
// Subclasses must define:
//   get _db()        -- return the Dexie database instance
//   get _tableName() -- return the log table name (e.g. "field_log", "chat_log")
//
// Stimulus values:
//   appName -- display name for copy output (e.g. "Field", "Chat")

const LEVEL_STYLES = {
  error: { dot: "bg-red-400",     text: "text-red-600",     badge: "bg-red-50 text-red-600"     },
  warn:  { dot: "bg-amber-400",   text: "text-amber-600",   badge: "bg-amber-50 text-amber-600" },
  info:  { dot: "bg-sky-400",     text: "text-sky-600",     badge: "bg-sky-50 text-sky-600"     },
  event: { dot: "bg-emerald-400", text: "text-emerald-600", badge: "bg-emerald-50 text-emerald-600" },
}

export default class extends Controller {
  static targets = ["list", "count", "filter", "copyBtn", "clearBtn"]
  static values  = { appName: { type: String, default: "App" } }

  async connect() {
    await this._render()
  }

  async filterChanged() {
    await this._render()
  }

  async copyLog() {
    const entries = await this._fetchEntries()
    const text = JSON.stringify(entries, null, 2)
    try {
      await navigator.clipboard.writeText(text)
      if (this.hasCopyBtnTarget) {
        const btn = this.copyBtnTarget
        const orig = btn.textContent
        btn.textContent = "Copied!"
        setTimeout(() => { btn.textContent = orig }, 2000)
      }
    } catch {
      window.prompt("Copy log:", text)
    }
  }

  async clearLog() {
    if (!confirm("Clear all diagnostic logs? This cannot be undone.")) return
    await this._logTable.clear()
    await this._render()
  }

  // -- override in subclasses --

  get _db() {
    throw new Error("Subclass must implement get _db()")
  }

  get _tableName() {
    throw new Error("Subclass must implement get _tableName()")
  }

  // -- private --

  get _logTable() {
    return this._db[this._tableName]
  }

  async _render() {
    const entries = await this._fetchEntries()

    if (this.hasCountTarget) {
      this.countTarget.textContent = entries.length
    }

    if (!this.hasListTarget) return

    if (entries.length === 0) {
      this.listTarget.innerHTML = `
        <div class="px-4 py-8 text-center">
          <p class="text-sm text-slate-400">No log entries yet.</p>
          <p class="text-xs text-slate-300 mt-1">Events will appear here as you use the app.</p>
        </div>
      `
      return
    }

    this.listTarget.innerHTML = entries.map(e => this._entryHtml(e)).join("")
  }

  async _fetchEntries() {
    const filter = this.hasFilterTarget ? this.filterTarget.value : "all"

    const entries = await this._logTable.orderBy("id").reverse().limit(200).toArray()

    if (filter === "all") return entries
    if (["error", "warn", "info", "event"].includes(filter)) {
      return entries.filter(e => e.level === filter)
    }
    return entries.filter(e => e.category === filter)
  }

  _entryHtml(entry) {
    const styles  = LEVEL_STYLES[entry.level] || LEVEL_STYLES.info
    const time    = new Date(entry.timestamp).toLocaleTimeString("en-AU", { timeStyle: "medium" })
    const date    = new Date(entry.timestamp).toLocaleDateString("en-AU", { dateStyle: "short" })
    const message = this._esc(entry.message)
    const detail  = entry.detail

    return `
      <div class="px-4 py-2.5 border-b border-slate-50 last:border-0">
        <div class="flex items-start gap-2.5">
          <span class="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full ${styles.dot}"></span>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded ${styles.badge}">${this._esc(entry.level)}</span>
              <span class="text-[10px] text-slate-400 font-mono">${this._esc(entry.category || "")}</span>
              <span class="text-[10px] text-slate-300 ml-auto">${date} ${time}</span>
            </div>
            <p class="text-xs text-slate-700 mt-0.5 break-words">${message}</p>
            ${detail ? `<pre class="text-[10px] text-slate-400 mt-0.5 whitespace-pre-wrap break-all font-mono">${this._esc(detail)}</pre>` : ""}
          </div>
        </div>
      </div>
    `
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
